// UltraFlex - PT Registration Page
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Phone, MapPin, Shield, FileText, Briefcase, ChevronRight, ChevronLeft, Check, Upload, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ['Personal Details', 'Business & Qualifications', 'Documents', 'Terms & Submit'];

export default function PTRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [ptId, setPtId] = useState(null);
  const [ptToken, setPtToken] = useState(null);
  const [docs, setDocs] = useState([]);

  const [form, setForm] = useState({
    email: '', password: '', confirm_password: '',
    first_name: '', last_name: '', phone: '', date_of_birth: '',
    address_line1: '', address_line2: '', city: '', postcode: '',
    emergency_contact_name: '', emergency_contact_phone: '',
    specialisations: [], insurance_provider: '', insurance_policy_number: '', insurance_expiry: '',
    bio: '', business_name: '', self_employed_utr: '',
    home_site_id: '', terms_accepted: false, gym_rules_accepted: false,
    commercial_model: '',
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetch(`${API}/sites`).then(r => r.json()).then(d => setSites(d.items || [])).catch(() => {});
  }, []);

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const toggleSpec = (spec) => {
    setForm(prev => ({
      ...prev,
      specialisations: prev.specialisations.includes(spec)
        ? prev.specialisations.filter(s => s !== spec)
        : [...prev.specialisations, spec]
    }));
  };

  const handleRegister = async () => {
    if (form.password !== form.confirm_password) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      const resp = await fetch(`${API}/pt/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Registration failed');
      setPtId(data.id);
      // Auto-login to get token for doc upload
      const loginResp = await fetch(`${API}/pt/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, password: form.password })
      });
      const loginData = await loginResp.json();
      if (loginResp.ok) setPtToken(loginData.access_token);
      setStep(2);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleDocUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);
    formData.append('description', file.name);
    try {
      const resp = await fetch(`${API}/pt/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${ptToken}` },
        body: formData,
      });
      const data = await resp.json();
      if (resp.ok) setDocs(prev => [...prev, { ...data, docType }]);
      else setError(data.detail || 'Upload failed');
    } catch (e) { setError('Upload failed'); }
  };

  const handleSubmitApplication = async () => {
    setLoading(true); setError('');
    try {
      const resp = await fetch(`${API}/pt/submit-application`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${ptToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ terms_accepted: form.terms_accepted, gym_rules_accepted: form.gym_rules_accepted }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Submission failed');
      setSuccess(true);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const specOptions = ['Strength Training', 'HIIT', 'Weight Loss', 'Bodybuilding', 'Yoga', 'Pilates', 'Boxing', 'CrossFit', 'Rehabilitation', 'Nutrition', 'Sports Performance', 'Elderly Fitness'];

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="pt-register-success">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-emerald-400" /></div>
          <h2 className="text-2xl font-bold text-white mb-2">Application Submitted!</h2>
          <p className="text-zinc-400 mb-6">Your PT application has been submitted for review. You'll be notified once it's been reviewed by management.</p>
          <button onClick={() => navigate('/pt/login')} className="w-full py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-semibold transition-colors" data-testid="pt-go-to-login">Go to PT Login</button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 py-12 px-4" data-testid="pt-register-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">PT Registration</h1>
          <p className="text-zinc-400">Join UltraFlex as a self-employed Personal Trainer</p>
        </div>

        {/* Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i <= step ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-500'}`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-xs hidden sm:block ${i <= step ? 'text-white' : 'text-zinc-500'}`}>{s}</span>
              {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-zinc-600" />}
            </div>
          ))}
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl p-3 mb-4 text-sm" data-testid="pt-register-error">{error}</div>}

        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          {/* Step 0: Personal Details */}
          {step === 0 && (
            <div className="space-y-4" data-testid="pt-step-personal">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><User className="w-5 h-5 text-red-500" /> Personal Details</h2>

              {/* PT Plan Selection */}
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Commercial Model *</label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => { updateForm('commercial_model', 'monthly_rent'); setFormErrors(p => ({...p, commercial_model: ''})); }}
                    className={`p-4 rounded-xl border text-left transition-all ${form.commercial_model === 'monthly_rent' ? 'border-red-500 bg-red-500/10' : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'}`}>
                    <p className="text-white font-semibold text-sm">Monthly Rent</p>
                    <p className="text-zinc-400 text-xs mt-1">Fixed monthly fee for gym access</p>
                    <p className="text-red-400 font-bold mt-2">£200/month</p>
                  </button>
                  <button type="button" onClick={() => { updateForm('commercial_model', 'ad_hoc'); setFormErrors(p => ({...p, commercial_model: ''})); }}
                    className={`p-4 rounded-xl border text-left transition-all ${form.commercial_model === 'ad_hoc' ? 'border-red-500 bg-red-500/10' : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'}`}>
                    <p className="text-white font-semibold text-sm">Ad-Hoc / Per Session</p>
                    <p className="text-zinc-400 text-xs mt-1">Pay per session via Stripe</p>
                    <p className="text-red-400 font-bold mt-2">£15/session</p>
                  </button>
                </div>
                {formErrors.commercial_model && <p className="text-red-400 text-xs mt-1">{formErrors.commercial_model}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">First Name *</label>
                  <input data-testid="pt-first-name" className={`w-full bg-zinc-800 border ${formErrors.first_name ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-3 py-2 text-white text-sm`} value={form.first_name} onChange={e => { updateForm('first_name', e.target.value); setFormErrors(p => ({...p, first_name: ''})); }} />
                  {formErrors.first_name && <p className="text-red-400 text-xs mt-1">{formErrors.first_name}</p>}
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Last Name *</label>
                  <input data-testid="pt-last-name" className={`w-full bg-zinc-800 border ${formErrors.last_name ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-3 py-2 text-white text-sm`} value={form.last_name} onChange={e => { updateForm('last_name', e.target.value); setFormErrors(p => ({...p, last_name: ''})); }} />
                  {formErrors.last_name && <p className="text-red-400 text-xs mt-1">{formErrors.last_name}</p>}
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Email *</label>
                <input data-testid="pt-email" type="email" className={`w-full bg-zinc-800 border ${formErrors.email ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-3 py-2 text-white text-sm`} value={form.email} onChange={e => { updateForm('email', e.target.value); setFormErrors(p => ({...p, email: ''})); }} />
                {formErrors.email && <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Password *</label>
                  <input data-testid="pt-password" type="password" className={`w-full bg-zinc-800 border ${formErrors.password ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-3 py-2 text-white text-sm`} value={form.password} onChange={e => { updateForm('password', e.target.value); setFormErrors(p => ({...p, password: ''})); }} />
                  {formErrors.password && <p className="text-red-400 text-xs mt-1">{formErrors.password}</p>}
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Confirm Password *</label>
                  <input data-testid="pt-confirm-password" type="password" className={`w-full bg-zinc-800 border ${formErrors.confirm_password ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-3 py-2 text-white text-sm`} value={form.confirm_password} onChange={e => { updateForm('confirm_password', e.target.value); setFormErrors(p => ({...p, confirm_password: ''})); }} />
                  {formErrors.confirm_password && <p className="text-red-400 text-xs mt-1">{formErrors.confirm_password}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs text-zinc-400 mb-1 block">Phone *</label>
                  <input data-testid="pt-phone" className={`w-full bg-zinc-800 border ${formErrors.phone ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-3 py-2 text-white text-sm`} value={form.phone} onChange={e => { updateForm('phone', e.target.value); setFormErrors(p => ({...p, phone: ''})); }} />
                  {formErrors.phone && <p className="text-red-400 text-xs mt-1">{formErrors.phone}</p>}
                </div>
                <div><label className="text-xs text-zinc-400 mb-1 block">Date of Birth</label><input data-testid="pt-dob" type="date" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" value={form.date_of_birth} onChange={e => updateForm('date_of_birth', e.target.value)} /></div>
              </div>
              <div><label className="text-xs text-zinc-400 mb-1 block">Address Line 1</label><input className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" value={form.address_line1} onChange={e => updateForm('address_line1', e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs text-zinc-400 mb-1 block">City</label><input className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" value={form.city} onChange={e => updateForm('city', e.target.value)} /></div>
                <div><label className="text-xs text-zinc-400 mb-1 block">Postcode</label><input className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" value={form.postcode} onChange={e => updateForm('postcode', e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs text-zinc-400 mb-1 block">Emergency Contact</label><input className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" value={form.emergency_contact_name} onChange={e => updateForm('emergency_contact_name', e.target.value)} /></div>
                <div><label className="text-xs text-zinc-400 mb-1 block">Emergency Phone</label><input className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" value={form.emergency_contact_phone} onChange={e => updateForm('emergency_contact_phone', e.target.value)} /></div>
              </div>
              <div><label className="text-xs text-zinc-400 mb-1 block">Home Gym *</label>
                <select data-testid="pt-home-site" className={`w-full bg-zinc-800 border ${formErrors.home_site_id ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-3 py-2 text-white text-sm`} value={form.home_site_id} onChange={e => { updateForm('home_site_id', e.target.value); setFormErrors(p => ({...p, home_site_id: ''})); }}>
                  <option value="">Select your primary gym...</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {formErrors.home_site_id && <p className="text-red-400 text-xs mt-1">{formErrors.home_site_id}</p>}
              </div>
              <button data-testid="pt-next-step-1" onClick={() => {
                const errs = {};
                if (!form.commercial_model) errs.commercial_model = 'Please select a commercial model';
                if (!form.first_name.trim()) errs.first_name = 'First name is required';
                if (!form.last_name.trim()) errs.last_name = 'Last name is required';
                if (!form.email.trim()) errs.email = 'Email is required';
                else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Please enter a valid email';
                if (!form.password) errs.password = 'Password is required';
                else if (form.password.length < 8) errs.password = 'Must be at least 8 characters';
                if (form.password !== form.confirm_password) errs.confirm_password = 'Passwords do not match';
                if (!form.phone.trim()) errs.phone = 'Phone is required';
                setFormErrors(errs);
                if (Object.keys(errs).length > 0) { setError('Please correct the highlighted fields.'); return; }
                setError(''); setStep(1);
              }} className="w-full py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors">Next <ChevronRight className="w-4 h-4" /></button>
            </div>
          )}

          {/* Step 1: Business & Qualifications */}
          {step === 1 && (
            <div className="space-y-4" data-testid="pt-step-business">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Briefcase className="w-5 h-5 text-red-500" /> Business & Qualifications</h2>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs text-zinc-400 mb-1 block">Business Name</label><input className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" value={form.business_name} onChange={e => updateForm('business_name', e.target.value)} /></div>
                <div><label className="text-xs text-zinc-400 mb-1 block">UTR (Self-Employed)</label><input className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" value={form.self_employed_utr} onChange={e => updateForm('self_employed_utr', e.target.value)} /></div>
              </div>
              <div><label className="text-xs text-zinc-400 mb-1 block">Bio / About You</label><textarea className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm h-20" value={form.bio} onChange={e => updateForm('bio', e.target.value)} /></div>
              <div><label className="text-xs text-zinc-400 mb-2 block">Specialisations</label>
                <div className="flex flex-wrap gap-2">{specOptions.map(s => (
                  <button key={s} onClick={() => toggleSpec(s)} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${form.specialisations.includes(s) ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>{s}</button>
                ))}</div>
              </div>
              <h3 className="text-sm font-semibold text-white flex items-center gap-2 mt-4"><Shield className="w-4 h-4 text-red-500" /> Insurance Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs text-zinc-400 mb-1 block">Insurance Provider</label><input className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" value={form.insurance_provider} onChange={e => updateForm('insurance_provider', e.target.value)} /></div>
                <div><label className="text-xs text-zinc-400 mb-1 block">Policy Number</label><input className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" value={form.insurance_policy_number} onChange={e => updateForm('insurance_policy_number', e.target.value)} /></div>
              </div>
              <div><label className="text-xs text-zinc-400 mb-1 block">Insurance Expiry</label><input type="date" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" value={form.insurance_expiry} onChange={e => updateForm('insurance_expiry', e.target.value)} /></div>
              <div className="flex gap-3 mt-4">
                <button onClick={() => setStep(0)} className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2"><ChevronLeft className="w-4 h-4" /> Back</button>
                <button data-testid="pt-register-submit" onClick={handleRegister} disabled={loading} className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50">{loading ? 'Registering...' : 'Register & Continue'} <ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}

          {/* Step 2: Documents */}
          {step === 2 && (
            <div className="space-y-4" data-testid="pt-step-documents">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><FileText className="w-5 h-5 text-red-500" /> Compliance Documents</h2>
              <p className="text-zinc-400 text-sm">Upload your qualification and insurance certificates. These are required before your application can be reviewed.</p>
              {[{ type: 'qualification_cert', label: 'Qualification Certificate', desc: 'Level 3+ PT qualification' }, { type: 'insurance_cert', label: 'Insurance Certificate', desc: 'Professional indemnity / public liability' }, { type: 'id_proof', label: 'ID Proof (Optional)', desc: 'Passport, driving licence, etc.' }].map(dt => {
                const uploaded = docs.find(d => d.document_type === dt.type);
                return (
                  <div key={dt.type} className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
                    <div className="flex items-center justify-between">
                      <div><p className="text-white text-sm font-medium">{dt.label}</p><p className="text-zinc-500 text-xs">{dt.desc}</p></div>
                      {uploaded ? (
                        <div className="flex items-center gap-2 text-emerald-400 text-xs"><Check className="w-4 h-4" /> Uploaded</div>
                      ) : (
                        <label className="cursor-pointer px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-xs rounded-lg flex items-center gap-1 transition-colors" data-testid={`pt-upload-${dt.type}`}><Upload className="w-3 h-3" /> Upload<input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => handleDocUpload(e, dt.type)} /></label>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="flex gap-3 mt-4">
                <button onClick={() => setStep(3)} className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2">Continue to Terms <ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}

          {/* Step 3: Terms & Submit */}
          {step === 3 && (
            <div className="space-y-4" data-testid="pt-step-terms">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Shield className="w-5 h-5 text-red-500" /> Terms & Submission</h2>
              <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.terms_accepted} onChange={e => updateForm('terms_accepted', e.target.checked)} className="mt-1 accent-red-600" data-testid="pt-terms-checkbox" />
                  <span className="text-zinc-300 text-sm">I accept the UltraFlex <span className="text-red-400">PT Terms and Conditions</span>, including the self-employed PT agreement and liability acknowledgement.</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.gym_rules_accepted} onChange={e => updateForm('gym_rules_accepted', e.target.checked)} className="mt-1 accent-red-600" data-testid="pt-rules-checkbox" />
                  <span className="text-zinc-300 text-sm">I accept the UltraFlex <span className="text-red-400">Gym Operating Rules and Policies</span> for personal trainers.</span>
                </label>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-4 text-xs text-zinc-500">
                <p>By submitting, your application will be reviewed by UltraFlex management. You'll receive a notification once approved. Until approval, you won't have active gym access.</p>
              </div>
              <div className="flex gap-3 mt-4">
                <button onClick={() => setStep(2)} className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2"><ChevronLeft className="w-4 h-4" /> Back</button>
                <button data-testid="pt-submit-application" onClick={handleSubmitApplication} disabled={loading || !form.terms_accepted || !form.gym_rules_accepted} className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-semibold disabled:opacity-50 transition-colors">{loading ? 'Submitting...' : 'Submit Application'}</button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-zinc-500 text-sm mt-6">Already registered? <Link to="/pt/login" className="text-red-400 hover:text-red-300">PT Login</Link></p>
      </div>
    </div>
  );
}
